import React from 'react';
import { ArrowLeft, Heart, Users, Globe, Award, Eye, Lightbulb, Target } from 'lucide-react';
import { goHome, navigate } from '../utils/navigation';

const AboutPage: React.FC = () => {
  const goBack = () => {
    goHome();
  };

  const goToRegistration = () => {
    navigate('/signup');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-red-50 to-pink-50">
      {/* Header Section */}
      <section className="py-12 bg-gradient-to-r from-dpn-raspberry to-dpn-pink">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={goBack}
            className="inline-flex items-center text-white hover:text-gray-200 transition-colors duration-200 mb-6 font-lucy-rose font-medium"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Home
          </button>
          <div className="text-center text-white">
            <h1 className="font-runtime text-4xl md:text-5xl font-bold mb-4">About Durga Puja Network</h1>
            <p className="font-lucy-rose text-xl opacity-95 max-w-3xl mx-auto">
              Celebrating and preserving the world's most vibrant cultural heritage through community, art, and tradition
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-runtime text-3xl md:text-4xl font-bold text-black mb-8">Our Mission</h2>
          <p className="font-lucy-rose text-xl text-gray-700 leading-relaxed mb-8">
            Durga Puja Network exists to celebrate, preserve, and share the rich cultural heritage of Durga Puja as recognized by UNESCO. We're building a global platform that connects communities, showcases artistry, and ensures this magnificent tradition continues to thrive across generations and continents.
          </p>
          <div className="bg-gradient-to-r from-dpn-pink/10 to-dpn-raspberry/10 rounded-2xl p-8">
            <blockquote className="font-runtime text-2xl font-bold text-dpn-raspberry mb-4">
              "Intangible Cultural Heritage of Humanity is our Durga Puja"
            </blockquote>
            <cite className="font-lucy-rose text-lg text-gray-600">— UNESCO</cite>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-runtime text-3xl md:text-4xl font-bold text-black mb-6">Our Story</h2>
              <div className="space-y-6 font-lucy-rose text-lg text-gray-700 leading-relaxed">
                <p>
                  Founded in 2025, Durga Puja Network emerged from a simple yet powerful vision: to create a digital home for the world's most vibrant cultural celebration. What started as a local initiative in Kolkata has grown into a global movement connecting millions of people across continents.
                </p>
                <p>
                  We recognized that Durga Puja is more than a festival—it's art, culture, cuisine, and community coming together in perfect harmony. Our platform celebrates the creative force, storytelling traditions, and vibrant community spirit that makes this UNESCO-recognized cultural heritage truly special.
                </p>
                <p>
                  Today, we're proud to serve as the bridge between traditional celebrations and modern digital connectivity, ensuring that the essence of Durga Puja reaches every corner of the world while preserving its authentic cultural roots.
                </p>
              </div>
            </div>
            <div className="relative">
              <img 
                src="/gallery/durga-puja-1.jpg" 
                alt="Traditional Durga Puja celebration" 
                className="rounded-2xl shadow-xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-dpn-pink/20 to-transparent rounded-2xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-runtime text-3xl md:text-4xl font-bold text-black mb-4">Our Values</h2>
            <p className="font-lucy-rose text-lg text-gray-600 max-w-2xl mx-auto">
              The principles that guide our mission to preserve and celebrate cultural heritage
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center p-6 rounded-xl bg-gray-50 hover:bg-white hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
              <div className="flex justify-center mb-4">
                <Heart className="h-8 w-8 text-dpn-raspberry" />
              </div>
              <h3 className="font-runtime text-xl font-bold text-black mb-3">Cultural Preservation</h3>
              <p className="font-lucy-rose text-gray-600 leading-relaxed">
                Safeguarding the rich traditions and intangible heritage of Durga Puja for future generations.
              </p>
            </div>

            <div className="text-center p-6 rounded-xl bg-gray-50 hover:bg-white hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
              <div className="flex justify-center mb-4">
                <Users className="h-8 w-8 text-dpn-light-green" />
              </div>
              <h3 className="font-runtime text-xl font-bold text-black mb-3">Committee Unity</h3>
              <p className="font-lucy-rose text-gray-600 leading-relaxed">
                Bringing together diverse communities worldwide through shared cultural experiences and celebrations.
              </p>
            </div>

            <div className="text-center p-6 rounded-xl bg-gray-50 hover:bg-white hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
              <div className="flex justify-center mb-4">
                <Globe className="h-8 w-8 text-dpn-olive" />
              </div>
              <h3 className="font-runtime text-xl font-bold text-black mb-3">Global Connection</h3>
              <p className="font-lucy-rose text-gray-600 leading-relaxed">
                Creating bridges between cultures and connecting Durga Puja celebrations across continents.
              </p>
            </div>

            <div className="text-center p-6 rounded-xl bg-gray-50 hover:bg-white hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
              <div className="flex justify-center mb-4">
                <Award className="h-8 w-8 text-dpn-pink" />
              </div>
              <h3 className="font-runtime text-xl font-bold text-black mb-3">Excellence in Art</h3>
              <p className="font-lucy-rose text-gray-600 leading-relaxed">
                Celebrating and promoting the incredible artistry and craftsmanship behind every celebration.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Impact Section */}
      <section className="py-16 bg-gradient-to-br from-dpn-light-green/10 to-dpn-olive/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Vision */}
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="flex items-center mb-6">
                <Eye className="h-8 w-8 text-dpn-olive mr-3" />
                <h3 className="font-runtime text-2xl font-bold text-black">Our Vision</h3>
              </div>
              <p className="font-lucy-rose text-lg text-gray-700 leading-relaxed mb-6">
                To become the world's premier digital platform for cultural heritage preservation, where every Durga Puja celebration—from the grandest pandals of Kolkata to intimate community gatherings in distant lands—finds its place in our global cultural archive.
              </p>
              <div className="space-y-3">
                <div className="flex items-center">
                  <Target className="h-5 w-5 text-dpn-pink mr-2" />
                  <span className="font-lucy-rose text-gray-700">Global cultural connectivity</span>
                </div>
                <div className="flex items-center">
                  <Target className="h-5 w-5 text-dpn-pink mr-2" />
                  <span className="font-lucy-rose text-gray-700">Heritage preservation</span>
                </div>
                <div className="flex items-center">
                  <Target className="h-5 w-5 text-dpn-pink mr-2" />
                  <span className="font-lucy-rose text-gray-700">Committee empowerment</span>
                </div>
              </div>
            </div>

            {/* Impact */}
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="flex items-center mb-6">
                <Lightbulb className="h-8 w-8 text-dpn-raspberry mr-3" />
                <h3 className="font-runtime text-2xl font-bold text-black">Our Impact</h3>
              </div>
              <p className="font-lucy-rose text-lg text-gray-700 leading-relaxed mb-6">
                Since our inception, we've connected thousands of communities, preserved countless cultural moments, and created a thriving ecosystem where tradition meets innovation in the most beautiful way possible.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 bg-dpn-pink/10 rounded-lg">
                  <div className="font-runtime text-2xl font-bold text-dpn-raspberry">500+</div>
                  <div className="font-lucy-rose text-sm text-gray-600">Communities Connected</div>
                </div>
                <div className="text-center p-4 bg-dpn-light-green/10 rounded-lg">
                  <div className="font-runtime text-2xl font-bold text-dpn-olive">50+</div>
                  <div className="font-lucy-rose text-sm text-gray-600">Countries Reached</div>
                </div>
                <div className="text-center p-4 bg-dpn-raspberry/10 rounded-lg">
                  <div className="font-runtime text-2xl font-bold text-dpn-pink">10K+</div>
                  <div className="font-lucy-rose text-sm text-gray-600">Cultural Moments</div>
                </div>
                <div className="text-center p-4 bg-dpn-olive/10 rounded-lg">
                  <div className="font-runtime text-2xl font-bold text-dpn-light-green">100K+</div>
                  <div className="font-lucy-rose text-sm text-gray-600">Global Members</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-runtime text-3xl md:text-4xl font-bold text-black mb-4">Meet Our Team</h2>
            <p className="font-lucy-rose text-lg text-gray-600 max-w-2xl mx-auto">
              Passionate individuals dedicated to preserving and celebrating cultural heritage
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 justify-center">
            <div className="text-center bg-gray-50 rounded-2xl p-8 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
              <img 
                src="/gallery/aboutUs/tanmay.jpg" 
                alt="Tanmay Sar" 
                className="w-24 h-24 rounded-full mx-auto mb-4 object-cover"
              />
              <h3 className="font-runtime text-xl font-bold text-black mb-2">Tanmay Sar</h3>
              <p className="font-lucy-rose text-dpn-raspberry font-medium mb-3">Co-founder</p>
              <p className="font-lucy-rose text-gray-600 text-sm">Media Professional with 20+ years in Sales & Marketing</p>
            </div>

            <div className="text-center bg-gray-50 rounded-2xl p-8 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
              <img 
                src="/gallery/aboutUs/Sharmistha.jpg" 
                alt="Sharmistha Sar" 
                className="w-24 h-24 rounded-full mx-auto mb-4 object-cover"
              />
              <h3 className="font-runtime text-xl font-bold text-black mb-2">Sharmistha Sar</h3>
              <p className="font-lucy-rose text-dpn-raspberry font-medium mb-3">Co-founder</p>
              <p className="font-lucy-rose text-gray-600 text-sm">20+ years of professional experience in business & operations</p>
            </div>
          </div>
        </div>
      </section>

      {/* Durga Puja Songs & CTA Section */}
      <section className="py-16 bg-gradient-to-r from-dpn-pink to-dpn-raspberry">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
          <div className="text-center mb-12">
            <h2 className="font-runtime text-3xl md:text-4xl font-bold text-white mb-4">
              See our Durga Puja Songs created by Bong Haat
            </h2>
            <p className="font-lucy-rose text-xl text-white/95 max-w-2xl mx-auto">
              Experience the musical celebration of our cultural heritage
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Song 1 */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
              <div className="aspect-video mb-4 rounded-xl overflow-hidden">
                <iframe 
                  width="100%" 
                  height="100%" 
                  src="https://www.youtube.com/embed/OGKbO1l6Vfo" 
                  title="Dugga Dugga Bola Chai" 
                  frameBorder="0" 
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
                  allowFullScreen
                  className="rounded-xl"
                ></iframe>
              </div>
              <h3 className="font-runtime text-xl font-bold text-white mb-2 text-center">Dugga Dugga Bola Chai</h3>
              <p className="font-lucy-rose text-white/90 text-sm mb-4 text-center">The anthem of celebration and joy</p>
            </div>

            {/* Song 2 */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
              <div className="aspect-video mb-4 rounded-xl overflow-hidden">
                <iframe 
                  width="100%" 
                  height="100%" 
                  src="https://www.youtube.com/embed/qpg4Fyf1gn4" 
                  title="Sobar Pujo Dugga Pujo" 
                  frameBorder="0" 
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
                  allowFullScreen
                  className="rounded-xl"
                ></iframe>
              </div>
              <h3 className="font-runtime text-xl font-bold text-white mb-2 text-center">Sobar Pujo Dugga Pujo</h3>
              <p className="font-lucy-rose text-white/90 text-sm mb-4 text-center">Everyone's celebration, Durga's celebration</p>
            </div>

            {/* Song 3 */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
              <div className="aspect-video mb-4 rounded-xl overflow-hidden">
                <iframe 
                  width="100%" 
                  height="100%" 
                  src="https://www.youtube.com/embed/FPvYd4kjwF8" 
                  title="Ebar Pujo Onno Bhabe" 
                  frameBorder="0" 
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
                  allowFullScreen
                  className="rounded-xl"
                ></iframe>
              </div>
              <h3 className="font-runtime text-xl font-bold text-white mb-2 text-center">Ebar Pujo Onno Bhabe</h3>
              <p className="font-lucy-rose text-white/90 text-sm mb-4 text-center">This time, celebrate differently</p>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-runtime text-3xl font-bold text-white mb-4">Join Our Cultural Movement</h2>
          <p className="font-lucy-rose text-xl text-white/95 mb-8">
            Be part of a global community that celebrates, preserves, and shares the rich cultural heritage of Durga Puja
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={goToRegistration}
              className="bg-white text-dpn-raspberry hover:bg-gray-100 font-lucy-rose font-semibold px-8 py-3 rounded-full transition-colors duration-200"
            >
              Join the Network
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;